import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import hospitalsServices from "../../../../services/hospitalsServices";
import { PenLine, Plus, Search, Trash2 } from "lucide-react";
import DataTable from "../../../../widgets/DataTable";
import CustomizableModal from "../../../../widgets/Modal/CustomizableModal";
import CustomSnackbar from "../../Snackbar/SnackBar";
import areasServices from "../../../../services/areasServices";

export default function AreasView() {
  const [areas, setAreas] = useState<any[]>([]);
  const [areasNames, setAreasNames] = useState<any[]>([]);
  const [visibleAreas, setVisibleAreas] = useState<Array<any>>([]);
  const [searchInput, setSearchInput] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAreas, setSelectedAreas] = useState<any[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [areaToEdit, setAreaToEdit] = useState<any | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMode, setSnackbarMode] = useState("");
  const [isModalLoading, setIsModalLoading] = useState(false);

  const handleSelectedAreasChange = (selected: any[]) => {
    setSelectedAreas(selected);
  };

  const unitsHeaders = [
    {
      name: "Nombre",
      id: "area_name",
    },
  ];

  const fetchAreas = () => {
    hospitalsServices
      .getAllActiveAreas()
      .then((res) => {
        setAreas(res);
        const areaNames = res.map((area: any) => area.area_name);
        setAreasNames(areaNames);
      })
      .catch((err) => {
        console.error("Error al obtener los cambios de la idea", err);
        setAreas([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchAreas();
  }, []);

  const areasWithActions = visibleAreas.map((area) => ({
    ...area,
    actions: [
      <IconButton
        key="edit"
        onClick={() => {
          setAreaToEdit(area);
          setShowEditModal(true);
        }}
        disableRipple
      >
        <PenLine className="menu-actions__edit-icon" />
      </IconButton>,
    ],
  }));

  useEffect(() => {
    const filtered = areas.filter((area) => {
      const matchesAreas =
        !searchInput?.trim() ||
        area.area_name.toLowerCase().includes(searchInput.toLowerCase());

      return matchesAreas;
    });
    setVisibleAreas(filtered);
  }, [searchInput, areas]);

  return (
    <Box>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flex: "9" }} className="header__searching__search-bar">
          <Box>
            <Search className="header__searching__search-bar__search-icon" />
          </Box>
          <Autocomplete
            freeSolo
            forcePopupIcon={false}
            fullWidth
            options={areasNames}
            inputValue={searchInput}
            onInputChange={(e, newInput, reason) => {
              if (reason === "clear") {
                setSearchInput("");
              } else {
                setSearchInput(newInput);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Buscar" />
            )}
          />
        </Box>
        <Box className="menu-actions">
          <IconButton onClick={() => setShowAddModal(true)} disableRipple>
            <Plus className="menu-actions__add-icon" />
          </IconButton>
          <IconButton
            disabled={selectedAreas?.length === 0}
            onClick={() => setShowDeleteModal(true)}
            disableRipple
          >
            <Trash2 className="menu-actions__trash-icon" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ marginTop: "20px" }}>
        <DataTable
          rows={areasWithActions}
          headers={unitsHeaders}
          isLoading={isLoading}
          tableType="checkbox"
          onSelectionChange={handleSelectedAreasChange}
        />
      </Box>
      <CustomizableModal
        isOpen={showAddModal}
        nextButton="Guardar"
        options={areasNames}
        modalType="add"
        panelView="area"
        cancel={() => setShowAddModal(false)}
        save={(value) => {
          if (value?.trim()) {
            areasServices
              .createArea({ name: value })
              .then(() => {
                setShowAddModal(false);
                fetchAreas();
              })
              .finally(() => {
                setSnackbarMode("add");
                setOpenSnackbar(true);
              });
          }
        }}
      />
      <CustomizableModal
        isOpen={showEditModal}
        initialValue={areaToEdit?.area_name}
        options={areasNames}
        nextButton="Guardar"
        modalType="edit"
        panelView="area"
        cancel={() => setShowEditModal(false)}
        save={(newName) => {
          if (areaToEdit && newName?.trim()) {
            areasServices
              .editArea(areaToEdit.id, {
                name: newName,
              })
              .then(() => {
                setShowEditModal(false);
                setAreaToEdit(null);
                fetchAreas();
              })
              .finally(() => {
                setSnackbarMode("edit");
                setOpenSnackbar(true);
              });
          }
        }}
      />
      <CustomizableModal
        isOpen={showDeleteModal}
        nextButton="Sí, eliminar"
        modalType="delete"
        panelView="area"
        cancel={() => setShowDeleteModal(false)}
        save={() => {
          if (selectedAreas?.length > 0) {
            Promise.all(
              selectedAreas.map((area) => areasServices.deactivateArea(area.id))
            )
              .then(() => {
                setShowDeleteModal(false);
                setSelectedAreas([]);
                fetchAreas();
              })
              .finally(() => {
                setSnackbarMode("delete");
                setOpenSnackbar(true);
              });
          }
        }}
      />
      <CustomSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        menu="areas"
        mode={snackbarMode}
      />
      {isModalLoading && (
        <Box className="loader-overlay">
          <Box className="loader-container">
            <Box className="custom-loader" />
          </Box>
        </Box>
      )}
    </Box>
  );
}
